import type { Profession } from "@brookbush/types";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import CertificationSelect from "./CertificationSelect";
import Button from "~/components/Button";
import SelectAProfessionDisclaimer from "~/components/SelectAProfessionDisclaimer";
import SelectAProfessionField from "~/components/SelectAProfessionField";
import TextField from "~/components/TextField";
import {
	useCertification,
	useCertifications,
	useSelectedProfessions,
} from "~/hooks";
import useUserFlags from "~/hooks/useUserFlags";

const CertificationOutOfScopeOverlay = () => {
	const { certification } = useCertification();
	const { certifications } = useCertifications();
	const {
		selectedProfessions,
		handleChangeProfession,
		isLoading: isLoadingSelectedProfessions,
	} = useSelectedProfessions();
	const { userFlags, updateFlag } = useUserFlags();

	const [primaryProfession, setPrimaryProfession] = useState<Profession | null>(
		null,
	);
	const [secondaryProfession, setSecondaryProfession] =
		useState<Profession | null>(null);
	const [showDisclaimer, setShowDisclaimer] = useState(false);
	const [isDisclaimerAccepted, setIsDisclaimerAccepted] = useState(false);
	const [cataMemberNumber, setCataMemberNumber] = useState<string>("");
	const [isOtherAcknowledged, setIsOtherAcknowledged] = useState(false);
	const [isDismissed, setIsDismissed] = useState(false);

	useEffect(() => {
		if (
			primaryProfession?.slug === "occupational-therapy" ||
			secondaryProfession?.slug === "occupational-therapy"
		) {
			setShowDisclaimer(true);
		} else {
			setShowDisclaimer(false);
			setIsDisclaimerAccepted(false);
		}
	}, [primaryProfession, secondaryProfession]);

	useEffect(() => {
		if (selectedProfessions?.primaryProfession?.slug !== "other") {
			setIsOtherAcknowledged(false);
		}
	}, [selectedProfessions]);

	const requiresCataMemberNumber =
		primaryProfession?.slug === "certified-athletic-therapist" ||
		secondaryProfession?.slug === "certified-athletic-therapist";

	const isOtherProfession =
		selectedProfessions?.primaryProfession?.slug === "other";

	const hasAcknowledgedOther =
		userFlags?.isOtherProfessionAcknowledged === true;

	const canSubmit =
		primaryProfession &&
		(requiresCataMemberNumber ? cataMemberNumber : true) &&
		(showDisclaimer ? isDisclaimerAccepted : true);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await handleChangeProfession({
			primaryProfession,
			secondaryProfession,
			cataMemberNumber: requiresCataMemberNumber ? cataMemberNumber : undefined,
		});
	};

	return (
		<AnimatePresence>
			{certification.outOfScope &&
				!isDismissed &&
				!(isOtherProfession && hasAcknowledgedOther) && (
					<motion.div
						initial={{
							opacity: 0,
							y: -10,
						}}
						animate={{
							opacity: 1,
							y: 0,
							transition: {
								duration: 0.2,
							},
						}}
						className="absolute inset-0 z-50 flex items-center justify-center bg-black/70"
					>
						<div className="m-4 flex max-w-2xl flex-col gap-6 rounded-xl bg-white p-7 sm:gap-7 sm:p-8 dark:bg-slate-800">
							<h1 className="text-center font-bold text-2xl">
								{!selectedProfessions?.primaryProfession
									? "Profession Not Selected"
									: isOtherProfession
										? "Before You Continue"
										: "Certification Out Of Scope"}
							</h1>

							{!selectedProfessions?.primaryProfession ? (
								<form onSubmit={handleSubmit} className="flex flex-col gap-6">
									<div className="rounded-xl bg-red-50 px-4 py-2 text-left dark:bg-red-300/20">
										<strong>Note:</strong> You have not selected a profession.
										Please select a primary profession to ensure accurate scope
										of practice information for the IMT.
									</div>
									<div className="flex flex-col gap-6 sm:py-4">
										<SelectAProfessionField
											value={primaryProfession}
											onChange={setPrimaryProfession}
											filterFn={(p) => p.slug !== secondaryProfession?.slug}
											label="Primary:"
											id="primary-profession"
											placeholder="Select a profession..."
										/>
										<SelectAProfessionField
											value={secondaryProfession}
											onChange={setSecondaryProfession}
											filterFn={(p) => p.slug !== primaryProfession?.slug}
											label="Secondary:"
											id="secondary-profession"
											placeholder="Optional"
											disabled={!primaryProfession}
										/>
										{requiresCataMemberNumber && (
											<div className="flex flex-col gap-2">
												<label
													htmlFor="cata-member-number"
													className="font-medium text-slate-700 text-sm dark:text-slate-200"
												>
													CATA Member Number
												</label>
												<TextField
													type="text"
													id="cata-member-number"
													value={cataMemberNumber}
													onChange={(e) => setCataMemberNumber(e.target.value)}
													placeholder="Enter your CATA member number"
												/>
											</div>
										)}
									</div>
									{showDisclaimer && (
										<SelectAProfessionDisclaimer
											disclaimer="In accordance with AOTA guidelines, to achieve satisfactory completion of all courses approved by the AOTA, you must reflect on all completed course materials and how they apply to your specific practice. If upon reflection you have any question(s), we advise you to leave your question(s) in the comment box at the bottom of the course. Questions are generally answered within 24 hours. Any question specific to the OT practice will be answered by a licensed OT with significant (10+ years) clinical experience."
											isDisclaimerAccepted={isDisclaimerAccepted}
											setIsDisclaimerAccepted={setIsDisclaimerAccepted}
										/>
									)}
									<Button
										variant="filled"
										disabled={!canSubmit || isLoadingSelectedProfessions}
										type="submit"
									>
										Save Profession
									</Button>
								</form>
							) : isOtherProfession ? (
								<>
									<p className="rounded-xl bg-orange-50 px-4 py-2 text-left dark:bg-orange-300/20">
										<strong>Note:</strong> These courses are designed and
										intended for licensed professionals who can legally perform
										manual therapy within their scope of practice. By
										continuing, you confirm that you hold the appropriate
										license or credentials to do so.
									</p>
									<SelectAProfessionDisclaimer
										text="I understand these courses are intended for licensed individuals who can legally perform manual therapy, and I confirm I meet this requirement. "
										isDisclaimerAccepted={isOtherAcknowledged}
										setIsDisclaimerAccepted={setIsOtherAcknowledged}
									/>

									<Button
										variant="filled"
										disabled={!isOtherAcknowledged}
										onClick={() => {
											updateFlag({ isOtherProfessionAcknowledged: true });
											setIsDismissed(true);
										}}
									>
										Agree &amp; Continue
									</Button>
								</>
							) : (
								<>
									<p className="rounded-xl bg-orange-50 px-4 py-2 text-left dark:bg-orange-300/20">
										<strong>Note:</strong> {certification.title} is not included
										in the scope of practice for the following professions: Yoga
										Instructors, Personal Trainers and Group Exercise
										Professionals.
									</p>
									<div className="relative mx-auto flex flex-col items-center gap-3 text-left font-medium text-lg sm:flex-row">
										Choose a different certification:{" "}
										<CertificationSelect certifications={certifications} />
									</div>
								</>
							)}
						</div>
					</motion.div>
				)}
		</AnimatePresence>
	);
};

export default CertificationOutOfScopeOverlay;
